import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif';
const INP = { width: '100%', padding: '10px 13px', border: 'none', borderRadius: 10, fontSize: 14, outline: 'none', background: '#F2F2F7', color: '#1C1C1E', fontFamily: FONT, boxSizing: 'border-box' };
const LBL = { display: 'block', fontSize: 11, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, fontFamily: FONT };

const CATEGORIES = ['Springs', 'Rollers', 'Tracks', 'Cables & Drums', 'Openers', 'Remotes', 'Panels', 'Hardware', 'Tools', 'Other'];

export default function ShipmentItemModal({ item, shipmentId, allocationMethod, onSave, onClose }) {
  const [form, setForm] = useState({
    productName: '', sku: '', category: 'Other',
    quantity: '', unitCost: '', weight: '', volume: '',
    dimL: '', dimW: '', dimH: '', dimUnit: 'cm',
    manualAllocationPct: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (item) {
      setForm({
        productName: item.productName || '',
        sku: item.sku || '',
        category: item.category || 'Other',
        quantity: item.quantity ?? '',
        unitCost: item.unitCost ?? '',
        weight: item.weight ?? '',
        volume: item.volume ?? '',
        dimL: item.dimensions?.l ?? '',
        dimW: item.dimensions?.w ?? '',
        dimH: item.dimensions?.h ?? '',
        dimUnit: item.dimensions?.unit || 'cm',
        manualAllocationPct: item.manualAllocationPct ?? '',
        notes: item.notes || '',
      });
    }
  }, [item]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  const qty = parseFloat(form.quantity) || 0;
  const cost = parseFloat(form.unitCost) || 0;
  const totalSupplierCost = qty * cost;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.productName.trim()) { setError('Product name is required.'); return; }
    if (!form.quantity || parseFloat(form.quantity) <= 0) { setError('Quantity must be greater than 0.'); return; }
    if (!form.unitCost || parseFloat(form.unitCost) < 0) { setError('Unit cost is required.'); return; }
    setSaving(true); setError('');
    try {
      await onSave({
        shipmentId,
        productName: form.productName.trim(),
        sku: form.sku.trim(),
        category: form.category,
        quantity: parseFloat(form.quantity) || 0,
        unitCost: parseFloat(form.unitCost) || 0,
        weight: parseFloat(form.weight) || 0,
        volume: parseFloat(form.volume) || 0,
        dimensions: { l: parseFloat(form.dimL)||0, w: parseFloat(form.dimW)||0, h: parseFloat(form.dimH)||0, unit: form.dimUnit },
        manualAllocationPct: parseFloat(form.manualAllocationPct) || 0,
        notes: form.notes,
      });
      onClose();
    } catch (err) { setError(err.message || 'Failed to save.'); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: FONT }}>
      <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '92vh', overflow: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.28)' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid #F2F2F7' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1C1C1E', margin: 0 }}>{item ? 'Edit Product' : 'Add Product to Shipment'}</h2>
          <button onClick={onClose} style={{ background: '#F2F2F7', border: 'none', cursor: 'pointer', borderRadius: '50%', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8E8E93' }}>
            <X size={15} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px 24px' }}>
          {error && <div style={{ background: '#FFF1F0', borderRadius: 10, padding: '10px 14px', color: '#FF3B30', fontSize: 13, marginBottom: 14 }}>{error}</div>}

          <div style={{ marginBottom: 14 }}>
            <label style={LBL}>Product Name *</label>
            <input type="text" value={form.productName} onChange={e => set('productName', e.target.value)} required style={INP} placeholder="e.g. Torsion Spring 200lb" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={LBL}>SKU</label>
              <input type="text" value={form.sku} onChange={e => set('sku', e.target.value)} style={INP} placeholder="e.g. TS-200" />
            </div>
            <div>
              <label style={LBL}>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} style={INP}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={LBL}>Quantity (units) *</label>
              <input type="number" min="1" step="1" value={form.quantity} onChange={e => set('quantity', e.target.value)} required style={INP} placeholder="e.g. 500" />
            </div>
            <div>
              <label style={LBL}>Supplier Cost / Unit ($) *</label>
              <input type="number" min="0" step="0.01" value={form.unitCost} onChange={e => set('unitCost', e.target.value)} required style={INP} placeholder="e.g. 5.00" />
            </div>
          </div>

          {totalSupplierCost > 0 && (
            <div style={{ background: '#F0FDF4', borderRadius: 10, padding: '8px 14px', marginBottom: 14, fontSize: 13, color: '#15803D', fontWeight: 500 }}>
              Total supplier cost: <b>${totalSupplierCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={LBL}>Weight / Unit (kg)</label>
              <input type="number" min="0" step="0.001" value={form.weight} onChange={e => set('weight', e.target.value)} style={INP} placeholder="e.g. 2.5" />
            </div>
            <div>
              <label style={LBL}>Volume / Unit (CBM)</label>
              <input type="number" min="0" step="0.0001" value={form.volume} onChange={e => set('volume', e.target.value)} style={INP} placeholder="e.g. 0.012" />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={LBL}>Dimensions (L × W × H)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8 }}>
              <input type="number" min="0" step="0.1" value={form.dimL} onChange={e => set('dimL', e.target.value)} style={INP} placeholder="L" />
              <input type="number" min="0" step="0.1" value={form.dimW} onChange={e => set('dimW', e.target.value)} style={INP} placeholder="W" />
              <input type="number" min="0" step="0.1" value={form.dimH} onChange={e => set('dimH', e.target.value)} style={INP} placeholder="H" />
              <select value={form.dimUnit} onChange={e => set('dimUnit', e.target.value)} style={{ ...INP, width: 'auto' }}>
                <option value="cm">cm</option>
                <option value="in">in</option>
              </select>
            </div>
          </div>

          {allocationMethod === 'manual' && (
            <div style={{ marginBottom: 14 }}>
              <label style={LBL}>Manual Allocation % (0–100)</label>
              <input type="number" min="0" max="100" step="0.1" value={form.manualAllocationPct} onChange={e => set('manualAllocationPct', e.target.value)} style={INP} placeholder="e.g. 25 for 25% of shared costs" />
              <div style={{ fontSize: 11, color: '#8E8E93', marginTop: 4 }}>All products in this shipment should add up to 100%</div>
            </div>
          )}

          <div style={{ marginBottom: 22 }}>
            <label style={LBL}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...INP, resize: 'vertical' }} placeholder="e.g. Spring type, gauge, color…" />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 20px', border: 'none', borderRadius: 12, background: '#F2F2F7', color: '#1C1C1E', cursor: 'pointer', fontSize: 14, fontWeight: 500, fontFamily: FONT }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '10px 24px', border: 'none', borderRadius: 12, background: '#84CC16', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.8 : 1, fontFamily: FONT }}>
              {saving ? 'Saving…' : item ? 'Update' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
